<script setup lang="ts">
import type { IMenuItem } from "@/types"
import { useQueryClient } from "@tanstack/vue-query"
import { computed, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { GET_EVENT_VAULT_QUERY_KEY, useGetEventVault } from "@/api"
import { CloseEventModal, EventLinksDrawer, FreemiumBadge, StartEventModal, ZipExportDrawer } from "@/components"
import { AppLink, Button, DropdownMenu, Icon } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { SidebarLayout } from "@/layouts"
import { EventVaultStatus, isEventWebsiteUrlModeEnabled } from "@/models"
import { useEventVaultStore } from "@/stores/eventVaultStore"

const eventVaultStore = useEventVaultStore()
const route = useRoute()
const router = useRouter()

const { t } = useTranslation()

const canvaTemplatesUrlByBrand: Record<string, string> = {
  guestpix: "https://guestpix.com/canva-templates/",
  honorpix: "https://honorpix.com/premium-canva-templates/",
}

function getCanvaTemplatesUrl(): string {
  const brand = import.meta.env.VITE_BRAND || "guestpix"
  return canvaTemplatesUrlByBrand[brand] ?? canvaTemplatesUrlByBrand.guestpix
}

const LayoutEventLinksDrawerKey = Symbol("LayoutEventLinksDrawer")
const LayoutZipExportDrawerKey = Symbol("LayoutZipExportDrawer")
const LayoutCloseEventModalKey = Symbol("LayoutCloseEventModal")
const LayoutStartEventModalKey = Symbol("LayoutStartEventModal")

const { toggle: toggleLinksDrawer } = useModal(LayoutEventLinksDrawerKey)
const { toggle: toggleExportDrawer } = useModal(LayoutZipExportDrawerKey)
const { toggle: toggleCloseModal } = useModal(LayoutCloseEventModalKey)
const { toggle: toggleStartModal } = useModal(LayoutStartEventModalKey)

const { data: eventVault, isLoading, refetch, isFetching } = useGetEventVault(eventVaultStore.eventVaultId, { include: ["zip", "website"] })

watch(eventVault, (vault) => {
  if (vault && vault.isHost === false) {
    router.push("/")
  }
}, { immediate: true })

const queryClient = useQueryClient()

async function onZipRequested() {
  await refetch()
  await queryClient.invalidateQueries({ queryKey: [GET_EVENT_VAULT_QUERY_KEY] })
}

const isSubscriptionEvent = computed(() => Boolean(eventVault.value?.packageSubscriptionId))

const eventClosable = computed(() => eventVault.value?.status === EventVaultStatus.Live && !isSubscriptionEvent.value)
const eventStartable = computed(() => (eventVault.value?.status === EventVaultStatus.Draft || eventVault.value?.status === EventVaultStatus.Ready) && !isSubscriptionEvent.value)
const eventInvitable = computed(() => eventVault?.value?.eventVaultSettings?.invitationEnabled !== undefined && eventVault?.value?.eventVaultSettings?.invitationEnabled !== null)
const advancedTemplateEnabled = computed(() => eventVault.value?.eventVaultSettings?.allowEventTemplate)
const websiteEnabled = computed(() => isEventWebsiteUrlModeEnabled(eventVault.value?.eventVaultSettings?.eventWebsiteUrlMode))
const eventThemeCustomisable = computed(() => (eventVault.value?.eventVaultSettings?.albumsEnabled || eventVault.value?.eventVaultSettings?.allowThemeEdit) && !advancedTemplateEnabled.value)
const noWishlistAccess = computed(() => eventVault.value?.eventVaultSettings?.wishlistMode === undefined || eventVault.value?.eventVaultSettings?.wishlistMode === null)

function hasActiveChild(children?: IMenuItem[]): boolean {
  if (!children?.length)
    return false
  return children.some(child =>
    (child.to && route.path === child.to)
    || (child.children && hasActiveChild(child.children)),
  )
}

function getMenuItemsWithActiveVariants(children: IMenuItem[] | undefined): IMenuItem[] | undefined {
  if (!children)
    return undefined

  return children.map(child => ({
    ...child,
    variant: child.to && route.path === child.to ? "highlighted" : child.variant,
  }))
}

const areFeaturesDisabled = computed(() => eventVault.value?.status === EventVaultStatus.Expired || isLoading.value)

const isFreemiumEvent = computed(() => eventVault.value?.freemiumMode)

const upgradeUrl = computed(() => {
  if (!isFreemiumEvent.value || !eventVault.value?.upgradeUrl)
    return

  return eventVault.value?.upgradeUrl
})

interface IEventNavItem extends IMenuItem {
  isNew?: boolean
  showFreemiumBadge?: boolean
}

const eventNavItems = computed<IEventNavItem[]>(() => {
  const invitationItems: IEventNavItem[] = [
    {
      key: "manage-invitation",
      label: t("sidebar_host_event_layout.link_label.manage_invitation"),
      to: `/event/${eventVaultStore.eventVaultId}/manage-invitation`,
      icon: "Mail",
    },
    {
      key: "manage-rsvp",
      label: t("sidebar_host_event_layout.link_label.rsvp_responses"),
      to: `/event/${eventVaultStore.eventVaultId}/rsvp-list`,
      icon: "Users",
    },
    {
      key: "manage-wishlist",
      label: t("sidebar_host_event_layout.link_label.manage_wishlist"),
      to: `/event/${eventVaultStore.eventVaultId}/wishlist`,
      icon: "Gift",
      isHidden: noWishlistAccess.value,
    },
  ]

  const manageItems: IEventNavItem[] = [
    {
      key: "manage-settings",
      label: t("sidebar_host_event_layout.link_label.manage_settings"),
      to: `/event/${eventVaultStore.eventVaultId}/advanced-settings`,
      icon: "SettingsCog",
    },
    {
      key: "edit-details",
      label: t("sidebar_host_event_layout.link_label.edit_details"),
      to: `/event/${eventVaultStore.eventVaultId}/edit`,
      icon: "CalendarLines",
    },
    {
      key: "view-guests",
      label: t("sidebar_host_event_layout.link_label.view_guests"),
      to: `/event/${eventVaultStore.eventVaultId}/guest-list`,
      icon: "Users",
    },
    {
      key: "manage-co-hosts",
      label: t("sidebar_host_event_layout.link_label.manage_co_hosts"),
      to: `/event/${eventVaultStore.eventVaultId}/users`,
      icon: "UserCog",
    },
    {
      key: "manage-reported-content",
      label: t("sidebar_host_event_layout.link_label.manage-reported-content"),
      to: `/event/${eventVaultStore.eventVaultId}/reported-content`,
      icon: "Flag",
    },
    {
      key: "website",
      label: t("sidebar_host_event_layout.link_label.website"),
      to: `/event/${eventVaultStore.eventVaultId}/website`,
      icon: "Globe",
      isHidden: !websiteEnabled.value,
    },
    {
      key: "close-event",
      label: t("sidebar_host_event_layout.link_label.close_event"),
      icon: "CalendarRemove",
      onClick: () => toggleCloseModal(),
      dismissOnInteract: true,
      isHidden: !eventClosable.value,
    },
    {
      key: "start-event",
      label: t("sidebar_host_event_layout.link_label.start_event"),
      icon: "CalendarCheck",
      onClick: () => toggleStartModal(),
      dismissOnInteract: true,
      isHidden: !eventStartable.value,
    },
  ]

  const mediaItems: IEventNavItem[] = [
    {
      key: "upload-images",
      label: t("sidebar_host_event_layout.link_label.upload_images"),
      to: `/guest/${eventVaultStore.eventVaultId}/gallery?upload=true`,
      icon: "ImagePlus",
    },
    {
      key: "manage-media",
      label: t("sidebar_host_event_layout.link_label.manage_media"),
      to: `/event/${eventVaultStore.eventVaultId}/manage-media`,
      icon: "ImageEdit",
    },
    {
      key: "manage-albums",
      label: t("sidebar_host_event_layout.link_label.manage_albums"),
      to: `/event/${eventVaultStore.eventVaultId}/albums`,
      icon: "Folder",
      disabled: isFreemiumEvent.value,
      showFreemiumBadge: isFreemiumEvent.value,
    },
    {
      key: "create-slideshow",
      label: t("sidebar_host_event_layout.link_label.create_slideshow"),
      to: `/event/${eventVaultStore.eventVaultId}/slideshow`,
      icon: "MonitorPlay",
    },
    {
      key: "download-zip",
      label: t("sidebar_host_event_layout.link_label.download_zip"),
      icon: "FolderDownload",
      onClick: () => toggleExportDrawer(),
      dismissOnInteract: true,
    },
    {
      key: "print-templates",
      label: t("sidebar_host_event_layout.link_label.design_templates"),
      href: getCanvaTemplatesUrl(),
      icon: "QrCode",
    },
  ]

  const customiseItems: IMenuItem[] = [
    {
      key: "customise-theme",
      label: t("sidebar_host_event_layout.link_label.customize-colors"),
      to: `/event/${eventVaultStore.eventVaultId}/customize`,
      icon: "Palette",
      isHidden: !eventThemeCustomisable.value,
    },
    {
      key: "saved-templates",
      label: t("sidebar_host_event_layout.link_label.saved-templates"),
      to: `/event/${eventVaultStore.eventVaultId}/template/apply`,
      icon: "Bookmark",
      isHidden: !advancedTemplateEnabled.value,
    },
  ]

  const items: IEventNavItem[] = [
    {
      icon: "CircleInfo",
      key: "summary",
      label: t("sidebar_host_event_layout.link_label.summary"),
      to: `/event/${eventVaultStore.eventVaultId}/`,
    },
    {
      icon: "Slider",
      key: "manage",
      label: t("sidebar_host_event_layout.link_label.manage"),
      children: manageItems,
    },
    {
      icon: "ImageOn",
      key: "media",
      label: t("sidebar_host_event_layout.link_label.media"),
      children: mediaItems,
    },
    {
      icon: "Mail",
      key: "invitation",
      label: t("sidebar_host_event_layout.link_label.invitation"),
      children: invitationItems,
      isHidden: !eventInvitable.value,
    },
    {
      icon: "Palette",
      key: "appearance",
      label: "Theme",
      children: customiseItems,
      isHidden: !eventThemeCustomisable.value && !advancedTemplateEnabled.value,
    },
  ]

  return items
})
</script>

<template>
  <SidebarLayout :upgrade-url="upgradeUrl" :is-freemium-event="eventVault?.freemiumMode">
    <template #header>
      <div v-if="isLoading" class="gpx-skeleton h-10" />
      <div v-else class="flex items-center justify-between gap-4 min-w-0">
        <div class="flex items-center gap-3 min-w-0 flex-1">
          <h1 class="line-clamp-1 overflow-hidden text-ellipsis whitespace-normal text-center md:text-left shrink min-w-0" data-test="header-title">
            {{ eventVault?.title }}
          </h1>
        </div>
        <Button v-if="eventVault?.status !== EventVaultStatus.Archived" :disabled="areFeaturesDisabled" data-test="view-link-btn" class="shrink-0" @click="toggleLinksDrawer">
          <Icon icon="Link" size="sm" />
          <span class="sr-only md:not-sr-only md:whitespace-nowrap">{{ t("sidebar_host_event_layout.view_link_btn") }}</span>
        </Button>
      </div>
    </template>

    <template #content>
      <div class="font-comfortaa max-w-full overflow-x-auto bg-footer-bg sticky top-18.5 z-20" data-test="event-nav-bar">
        <ul v-if="eventVault?.status !== EventVaultStatus.Archived" class="flex items-center gap-0 flex-row">
          <li v-for="link in eventNavItems" :key="link.label" class="m-0 text-sm md:text-base" :data-test="`event-nav-item-${link.key}`">
            <div v-if="!link.isHidden">
              <AppLink
                v-if="link.to"
                :to="link.to"
                class="flex items-center gap-2 px-4 md:px-6 py-4 no-underline font-bold transition-all duration-200 ease-in-out border-b-[3px] border-transparent relative hover:border-b-nav-active"
                :class="[
                  link.key === 'summary' && route.path === link.to
                    ? 'font-black border-b-nav-active'
                    : 'border-transparent',
                ]"
                :data-test="`event-nav-link-${link.key}`"
              >
                <Icon v-if="link.icon" :icon="link.icon" class="text-nav-active" size="sm" />
                {{ link.label }}
              </AppLink>

              <DropdownMenu
                v-if="link.children"
                class="md:ml-auto"
                :items="getMenuItemsWithActiveVariants(link.children)"
                side="bottom"
                align="start"
                :disabled="areFeaturesDisabled"
              >
                <template #item-trailing="{ item }">
                  <FreemiumBadge v-if="(item as IEventNavItem).showFreemiumBadge" variant="icon" />
                </template>
                <template #activator="{ isOpen }">
                  <button
                    type="button"
                    class="flex items-center gap-2 px-4 md:px-6 py-4 no-underline font-bold relative cursor-pointer"
                    :class="[hasActiveChild(link.children) ? 'font-black border-b-nav-active' : 'border-transparent', {
                      'transition-all duration-200 ease-in-out border-b-[3px]  hover:border-b-nav-active': !areFeaturesDisabled,
                      'opacity-50 cursor-not-allowed': areFeaturesDisabled,
                    },
                    ]"
                    :data-test="`event-nav-menu-activator-${link.key}`"
                    :aria-disabled="areFeaturesDisabled"
                  >
                    <Icon v-if="link.icon" :icon="link.icon" class="p-0 -ml-1.25 text-nav-active" size="sm" />
                    {{ link.label }}
                    <Icon
                      icon="CircleChevronDown"
                      class="transition-transform duration-200 ease-in-out w-5 h-5"
                      :class="{ 'rotate-180': isOpen }"
                    />
                  </button>
                  <span v-if="link.isNew" class="text-[10px] bg-nav-active font-bold text-white font-montserrat h-fit rounded-lg px-2 py-1 leading-none mt-[2.5px] -ml-20 ">New</span>
                </template>
              </DropdownMenu>
            </div>
          </li>
        </ul>
      </div>

      <div class="container my-6" data-test="main-content-container">
        <slot :toggle-export-drawer="toggleExportDrawer" />
      </div>
    </template>
  </SidebarLayout>

  <EventLinksDrawer :is-loading="isLoading" :event-vault="eventVault" :drawer-key="LayoutEventLinksDrawerKey" />
  <ZipExportDrawer :is-loading="isLoading || isFetching" :event-vault="eventVault" :drawer-key="LayoutZipExportDrawerKey" @zip-requested="onZipRequested" />
  <CloseEventModal :is-loading="isLoading" :event-vault="eventVault" :drawer-key="LayoutCloseEventModalKey" />
  <StartEventModal :is-loading="isLoading" :event-vault="eventVault" :drawer-key="LayoutStartEventModalKey" />
</template>
